// Localization provider - manages multi-language support and translations
import React, { createContext, useContext, useState, useEffect } from 'react';
import { logger } from '../lib/utils/logger';

// Import translation files for all supported languages
import enTranslations from '../locales/en.json';
import frTranslations from '../locales/fr.json';
import ptTranslations from '../locales/pt.json';
import esTranslations from '../locales/es.json';

const translations = {
  en: enTranslations,
  fr: frTranslations,
  pt: ptTranslations,
  es: esTranslations
};

const LocalizationContext = createContext();

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
};

export const LocalizationProvider = ({ children }) => {
  // Language state - initialize from localStorage or default to English
  const [language, setLanguage] = useState(() => {
    // Try to get language from localStorage, fallback to 'en' (English) for login page
    // This ensures the login page always has a language, even when localStorage is empty
    return localStorage.getItem('app-language') || 'en';
  });

  // Translation and user preference state
  const [translationsLoaded, setTranslationsLoaded] = useState(false);
  const [userLanguageSet, setUserLanguageSet] = useState(() => {
    // Check if user has manually set language in localStorage
    return localStorage.getItem('user-manually-set-language') === 'true';
  });

  const [userLanguageDetermined, setUserLanguageDetermined] = useState(false);

  useEffect(() => {
    // Mark translations as loaded
    setTranslationsLoaded(true);
    
    // If user has manually set language, mark as determined
    if (userLanguageSet) {
      setUserLanguageDetermined(true);
    }
  }, [userLanguageSet]);

  // Debug language changes
  useEffect(() => {
    logger.debug('Language state changed', { language });
  }, [language]);

  const changeLanguage = (newLanguage) => {
    logger.debug('changeLanguage called', { newLanguage });
    if (translations[newLanguage]) {
      logger.debug('Setting language', { language: newLanguage });
      setLanguage(newLanguage);
      localStorage.setItem('app-language', newLanguage);
      localStorage.setItem('user-manually-set-language', 'true'); // Mark that user has manually set language
      setUserLanguageSet(true);
      setUserLanguageDetermined(true);
    } else {
      logger.warn('Language not supported', { language: newLanguage });
    }
  };

  const setLanguageFromUser = (user) => {
    logger.debug('setLanguageFromUser called', { userId: user?.id, userLanguageSet });
    if (userLanguageSet) {
      logger.debug('Skipping automatic language setting - user has manually set language');
      setUserLanguageDetermined(true);
      return;
    }
    if (user && user.commonLanguage && translations[user.commonLanguage]) {
      logger.debug('Setting language from user', { language: user.commonLanguage });
      setLanguage(user.commonLanguage);
      localStorage.setItem('app-language', user.commonLanguage);
      setUserLanguageDetermined(true);
      // Don't set the manual flag when setting from user profile
    } else {
      // If no user language is available, keep current language (should be 'en' from initialization)
      logger.debug('No user language available, keeping current language', { currentLanguage: language });
      setUserLanguageDetermined(true);
    }
  };

  const resetManualLanguageFlag = () => {
    logger.debug('Resetting manual language flag');
    setUserLanguageSet(false);
    localStorage.removeItem('user-manually-set-language');
  };

  const t = (key, params = {}) => {
    if (!translationsLoaded) {
      return key; // Return key while loading
    }

    const keys = key.split('.');
    let value = translations[language];

    // Navigate through nested keys
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to French if key not found
        value = translations.fr;
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            return key; // Return key if not found in fallback
          }
        }
        break;
      }
    }

    if (typeof value !== 'string') {
      return key;
    }

    // Replace parameters in the translation
    return value.replace(/\{\{(\w+)\}\}/g, (match, param) => {
      return params[param] !== undefined ? params[param] : match;
    });
  };

  const availableLanguages = [
    { code: 'en', name: 'English', flag: 'flag flag-xs flag-country-us' },
    { code: 'fr', name: 'Français', flag: 'flag flag-xs flag-country-fr' },
    { code: 'pt', name: 'Português', flag: 'flag flag-xs flag-country-pt' },
    { code: 'es', name: 'Español', flag: 'flag flag-xs flag-country-es' }
  ];

  const value = {
    language,
    changeLanguage,
    setLanguageFromUser,
    resetManualLanguageFlag,
    t,
    availableLanguages,
    translationsLoaded,
    userLanguageDetermined
  };

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};
